<template>
  <div class="home-view">
    <div class="home-view-hero-background">
      <div class="home-view-hero-background-accent" :style="`background-image: ${backgroundImageHelper($urlProvider.GenerateHelloImageUrl('home_hero_background.png'))}`"></div>
    </div>

    <div class="home-view-hero-section">
      <h1 class="hero-title">
        <div>{{ $t('components.home_view.title_1') }}</div>
        <div>
          {{ $t('components.home_view.title_2') }} <span class="playfair-text">{{ $t('components.home_view.title_3') }}</span>
        </div>
      </h1>

      <div class="highlight-section" @click="routeToCard">
        <div v-for="(image, index) of images" :class="`highlight-card c${index + 1}`" :style="`background-image: url(${marketingCarouselCardImageHelper($urlProvider.GenerateHelloMarketingImageUrl(image.image))})`" :key="index"></div>
      </div>

      <p class="hero-sub-title">
        {{ $t('components.home_view.tag_line') }}
      </p>

      <div class="hero-call-to-action">
        <h-btn class="cta-btn" block color="secondary" to="cards" x-large inverted rounded>{{ $t('components.home_view.shop_btn') }}</h-btn>
      </div>
    </div>

    <div class="perk-card-list">
      <div v-for="(card, index) in perks_cards" class="perk-cards" :key="index">
        <h3 class="perk-card-title">{{ $t(card.title) }}</h3>
        <p class="h-text-2 perk-card-text">{{ $t(card.text) }}</p>
        <div class="perk-card-image" :style="`background-image: url(${card.image})`"></div>
      </div>
    </div>

    <div class="hellocard-info">
      <div class="receive-hello-card-section">
        <div class="text-section">
          <div class="receive-hello-card-section-title">
            {{ $t('components.home_view.info_title_1_1') }}
            <span class="title-accent">
              {{ $t('components.home_view.info_title_1_2') }}
            </span>
            ?
          </div>
          <div class="receive-hello-card-section-text">
            {{ $t('components.home_view.info_text_1') }}
          </div>
        </div>
        <div class="image-section"></div>
      </div>

      <div class="receive-hello-card-section-2">
        <div class="text-section">
          <div class="receive-hello-card-section-title">
            {{ $t('components.home_view.info_title_2_1') }}
            <span class="title-accent">{{ $t('components.home_view.info_title_2_2') }}</span>
            {{ $t('components.home_view.info_title_2_3') }}
          </div>
          <div class="receive-hello-card-section-text">{{ $t('components.home_view.info_text_2') }}</div>
        </div>
        <div class="image-section-2"></div>
      </div>
    </div>

    <div class="corporate-section">
      <div class="corporate-gift-card-table">
        <img class="corporate-gift-card-table-img" :alt="$t('components.home_view.corporate_section.table_alt')" :src="corpoTableSrc" />
      </div>

      <div class="corporate-cropped-container">
        <div class="container-bezier-curve">
          <img class="abstract-bezier-curve" src="~@/assets/home_corporate_section/abstract_bezier_curve.svg" />
        </div>

        <img class="business-man" :alt="$t('components.home_view.corporate_section.business_man_alt')" :src="$urlProvider.GenerateHelloCorpoImageUrl('hello_corporate_program_business_man_v1.png')" />
      </div>

      <div class="corporate-info">
        <div class="corporate-info-content">
          <h3>{{ $t('components.home_view.corporate_section.title') }}</h3>

          <p>
            {{ $t('components.home_view.corporate_section.text') }}
          </p>

          <div class="corporate-cta">
            <h-btn class="btn" block color="secondary" to="corpo" large rounded>{{ $t('components.home_view.corporate_section.cta') }}</h-btn>
          </div>
        </div>
      </div>
    </div>
    <div class="faq">
      <h2 class="faq-title">{{ $t('components.home_view.faq_title') }}</h2>

      <v-expansion-panels flat multiple class="expansion-content">
        <v-expansion-panel style="background-color: transparent; color: var(--color-grey-3)" v-for="(question, index) of questions" :key="index">
          <v-expansion-panel-header hide-actions style="padding: 0px">
            <template v-slot:default="{ open }">
              <div class="header-container">
                <span class="header-text">{{ $t(question.question) }}</span>
                <v-icon color="var(--color-grey-3)" class="header-icon" size="31">{{ open ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
              </div>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <span v-html="$t(question.answer, { faqUrl })"></span>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <p class="faq-footer">
        <span style="vertical-align: middle">{{ $t('components.home_view.faq') }}</span>
        <a class="faq-link" :href="faqUrl" target="_blank">{{ $t('components.home_view.faq_cta') }}</a>
        <span style="vertical-align: middle">{{ $t('components.home_view.faq_cta_suffix') }}</span>
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import _shuffle from 'lodash/shuffle';
import { marketingCarouselCardImageHelper, backgroundImageHelper } from '@/composables/image-helper';

export default {
  setup() {
    return {
      marketingCarouselCardImageHelper,
      backgroundImageHelper,
    };
  },
  title: (args) => args.$t('page_title.home'),
  props: {
    openOverlay: {
      type: Boolean,
    },
    openRegister: {
      type: Boolean,
    },
  },
  async created() {
    // MIGRATION (PLP): This is to ensure redirection for old link
    if (this.$route.query['email-verification-token']) {
      return this.$router.pushNoDuplicate({ name: 'email-verification', query: { ...this.$route.query }, params: { _position: { x: 0, y: window.scrollY } } });
    }
    if (this.openOverlay) {
      this.openHeaderDrawerWithParams({
        content: 'auth',
        payload: {},
      });
    }
  },
  data() {
    return {
      images: _shuffle([
        { partner_id: 'alberta-avenue', image: 'Card-Alberta-Avenue-Business-Association.png' },
        { partner_id: 'ccibdc', image: 'Card-Baie-des-Chaleurs.png' },
        { partner_id: 'becancour', image: 'Card-Bécancour.png' },
        { partner_id: 'centrevillesainthyacinthe', image: 'Card-Centre-Ville-de-Saint-Hyacinthe.png' },
        { partner_id: 'champlainplace', image: 'Card-Champlain.png' },
        { partner_id: 'crescentheightsvillage', image: 'Card-Crescent-Heights.png' },
        { partner_id: 'downtownnorthbay', image: 'Card-Downtown-North-Bay.png' },
        { partner_id: 'downtownrenfrewbia', image: 'Card-Downtown-Renfrew-BIA.png' },
        { partner_id: 'downtowndundas', image: 'Card-Dowtown-Dundas-BIA.png' },
        { partner_id: 'fairviewpark', image: 'Card-Fairview-Park.png' },
        { partner_id: 'laurierouest', image: 'Card-Laurier-Ouest.png' },
        { partner_id: 'marchespublics-mtl', image: 'Card-Marchés-Public-de-Montréal.png' },
        { partner_id: 'tourisme-monteregie', image: 'Card-Monteregie.png' },
        { partner_id: 'mrc-des-appalaches', image: 'Card-MRC-des-Appalaches.png' },
        { partner_id: 'plazasthubert', image: 'Card-Plaza-St-Hubert.png' },
        { partner_id: 'promenade-wellington', image: 'Card-Promenade-Wellington.png' },
        { partner_id: 'quebecvacances', image: 'Card-Quebec-Vacances.png' },
        { partner_id: 'ccisf', image: 'Card-Saguenay-le-Fjord.png' },
        { partner_id: 'valleyfield', image: 'Card-Salaberry-de-Valleyfrield.png' },
        { partner_id: 'sdc-cotedesneiges', image: 'Card-SDC-Cote-des-Neiges.png' },
        { partner_id: 'shediacdowntown', image: 'Card-Shediac.png' },
        { partner_id: 'sherbrooke', image: 'Card-Sherbrooke.png' },
        { partner_id: 'southamptonbia', image: 'Card-Southampton.png' },
        { partner_id: 'tetro', image: 'Card-Tétreaultville.png' },
        { partner_id: 'vaudreuil-soulanges', image: 'Card-Vaudreuil-Soulanges.png' },
        { partner_id: 'villagemontreal', image: 'Card-Village.png' },
        { partner_id: 'cominar', image: 'Card-Vitrine-Cominar.png' },
        { partner_id: 'centrevilledejoliette', image: 'Card-Centre-Ville-Joliette.png' },
        { partner_id: 'ccinb', image: 'Card-Beauce.png' },
        { partner_id: 'unionville', image: 'Card-Unionville.png' },
        { partner_id: 'newmarket', image: 'Card-Newmarket.png' },
        { partner_id: 'mrc-les-moulins', image: 'Card-MRC-Les-Moulins.png' },
        { partner_id: 'mach', image: 'Card-Mach.png' },
        { partner_id: 'espacestcharles', image: 'Card-Espace-St-Charles.png' },
      ]).slice(0, 7),
      questions: [
        {
          question: 'components.home_view.question_1',
          answer: 'components.home_view.answer_1',
        },
        {
          question: 'components.home_view.question_2',
          answer: 'components.home_view.answer_2',
        },
        {
          question: 'components.home_view.question_3',
          answer: 'components.home_view.answer_3',
        },
        {
          question: 'components.home_view.question_4',
          answer: 'components.home_view.answer_4',
        },
        {
          question: 'components.home_view.question_5',
          answer: 'components.home_view.answer_5',
        },
      ],
      perks_cards: [
        { title: 'components.home_view.card_title_1', text: 'components.home_view.card_text_1', image: require('@/assets/hello_home_page.png') },
        { title: 'components.home_view.card_title_2', text: 'components.home_view.card_text_2', image: require('@/assets/hello_home_page_2.png') },
        { title: 'components.home_view.card_title_3', text: 'components.home_view.card_text_3', image: require('@/assets/hello_home_page_3.png') },
        { title: 'components.home_view.card_title_4', text: 'components.home_view.card_text_4', image: require('@/assets/hello_home_page_4.png') },
      ],
    };
  },
  watch: {
    currentLocale() {
      this.$title = this.$t('page_title.home');
    },
  },
  computed: {
    ...mapGetters('account', ['canRequestCard', 'emailNeedVerification']),
    ...mapGetters('security', ['authenticated']),
    ...mapGetters('ui', ['currentLocale']),
    cardApplyRedirectLink() {
      return {
        url: window.location.href,
        label_fr: this.$t('components.card_application.redirect_link.return_to_home_label', 'fr'),
        label_en: this.$t('components.card_application.redirect_link.return_to_home_label', 'en'),
      };
    },
    faqUrl() {
      return this.currentLocale === 'fr' ? this.$urlProvider.HelloFaqFr : this.$urlProvider.HelloFaqEn;
    },
    corpoTableSrc() {
      return this.currentLocale === 'fr' ? 'https://images.hellocard.ca/espace-hello/corporate/hello_corporate_table_fr_v1.png' : 'https://images.hellocard.ca/espace-hello/corporate/hello_corporate_table_en_v1.png';
    },
  },
  methods: {
    ...mapActions('overlay', ['openHeaderDrawerWithParams']),
    routeToCard() {
      this.$router.pushNoDuplicate({ name: 'cards' });
    },
  },
};
</script>

<style lang="scss" scoped>
.home-view {
  --color-text: #000;
  --color-grey-3: #737373;
  color: var(--color-text);
  padding-bottom: var(--gap);

  --hc-vertical-margin-separator: 0px;
  --hc-vertical-margin-separator-big: 0px;
  @media ($sm-min) {
    --hc-vertical-margin-separator: 60px;
    --hc-vertical-margin-separator-big: 96px;
  }
  @media ($md-min) {
    --hc-vertical-margin-separator: 48px;
    --hc-vertical-margin-separator-big: 112px;
  }
}

.home-view-hero-background {
  position: absolute;
  width: 100%;
  height: 680px;
  background: linear-gradient(180deg, #2e3191 80.95%, #f4f9ff 133.79%);

  @media ($sm-min) {
    height: 915px;
  }

  @media ($md-min) {
    height: 1150px;
  }

  @media ($lg-min) {
    height: 1050px;
  }

  @media ($xl-min) {
    height: 1400px;
  }
}

.home-view-hero-background-accent {
  height: 100%;
  // background: url('~@/assets/home_highlight/BG.png');
  background-repeat: no-repeat;
  background-attachment: scroll;

  background-size: calc(2.8 * 360px) calc(2.8 * 246.6px);
  background-position-x: calc(50% + 30px);
  background-position-y: -150px;

  @media ($sm-min) {
    background-size: calc(7 * 360px) calc(7 * 246.6px);
    background-position-x: calc(50% + 60px);
    background-position-y: -500px;
  }

  @media ($md-min) {
    background-size: initial;
    background-position-x: calc(50% + 135px);
    background-position-y: -680px;
  }

  @media ($lg-min) {
    background-size: initial;
    background-position-x: calc(50% + 135px);
    background-position-y: -700px;
  }

  @media ($xl-min) {
    background-size: initial;
    background-position-x: calc(50% + 135px);
    background-position-y: -500px;
  }
}

.home-view-hero-section {
  position: relative;
  max-width: 1255px;
  margin: 0 auto;
}

.perk-cards {
  display: flex;
  flex-direction: column;
  font-family: 'Apfel Grotezk';
  border-radius: 20px;
  box-shadow: 0px 8px 12px 0px rgba(106, 104, 173, 0.12);
  padding: var(--gap-large);
  color: var(--color-text);
  background-color: white;
}

.perk-card-list {
  max-width: var(--page-content-max-width);
  margin-left: auto;
  margin-right: auto;
  position: relative;
  padding: var(--gap);
  margin-bottom: 36px;

  & > *:not(:last-child) {
    margin-bottom: var(--gap-large);
  }

  @media ($sm-min) {
    display: grid;
    margin: var(--hc-vertical-margin-separator-big) auto;
    padding: 0 var(--gap);
    grid-template-columns: repeat(auto-fit, minmax(336px, 1fr));
    gap: 24px;
    & > *:not(:last-child) {
      margin-bottom: 0;
    }
  }

  @media ($md-min) {
    margin-top: 144px;
    margin-bottom: 72px;
    flex-wrap: nowrap;
    gap: 24px;
    padding: 0 var(--gap);
  }
  @media ($lg-min) {
    gap: 24px;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    margin-bottom: 112px;
  }
  @media ($xl-min) {
    margin-top: 332px;
  }
}

.perk-card-title {
  margin-bottom: var(--gap);
  font-size: 30px;

  @media ($sm-min) {
    font-size: 38px;
  }
  @media ($lg-min) {
    font-size: 32px;
  }
  @media ($xl-min) {
    font-size: 34px;
  }
}

.perk-card-image {
  justify-content: center;
  max-height: 55px;
  width: 100%;
  min-height: 55px;
  background-size: contain;
  background-position: center bottom;

  @media ($sm-min) {
    min-height: 120px;
  }

  @media ($md-min) {
    min-height: 80px;
  }

  @media ($lg-min) {
    min-height: 120px;
  }

  & > * {
    object-fit: contain;
  }
}

.perk-card-text {
  font-family: Barlow;
  flex-grow: 1;
  color: var(--color-grey-3);
  margin-bottom: 16px;
  font-size: 16px;
  @media ($md-min) {
    font-size: 20px;
  }
  @media ($lg-min) {
    font-size: 18px;
  }
}

.highlight-section {
  display: flex;
  margin: 0 auto;
  height: 179px;
  align-items: center;
  justify-content: center;
  gap: var(--gap);
  width: fit-content;
  @media ($sm-min) {
    height: 240px;
    gap: 28px;
  }

  @media ($md-min) {
    height: 300px;
  }

  @media ($lg-min) {
    height: 300px;
  }
}

.highlight-section:hover {
  cursor: pointer;
  & > *:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 7px;
    background-color: rgba(255, 255, 255, 0.1);
    @media ($sm-min) {
      border-radius: 20px;
    }

    @media ($md-min) {
      border-radius: 13px;
    }

    @media ($lg-min) {
      border-radius: 13px;
    }
  }
}

.highlight-card {
  position: relative;
  display: flex;
  width: max-content;
  background-size: cover;
  background-position: center;
  box-shadow: 0px 1.543px 50.157px 4.63px rgba(0, 0, 0, 0.25);
  border-radius: 7px;
  justify-self: center;
  z-index: 2;
  aspect-ratio: 0.6296;

  @media ($sm-min) {
    border-radius: 20px;
  }

  @media ($md-min) {
    border-radius: 13px;
  }

  @media ($lg-min) {
    border-radius: 13px;
  }

  &.c1 {
    justify-self: end;
  }
  &.c7 {
    justify-self: start;
  }
  &.c7,
  &.c1 {
    display: none;
    height: 60%;
    @media ($lg-min) {
      display: block;
    }
  }

  &.c6,
  &.c2 {
    display: none;
    height: 72%;
    @media (min-width: 820px) {
      display: block;
    }
    @media ($md-min) {
      display: block;
    }
    @media ($lg-min) {
      display: block;
    }
  }

  &.c5,
  &.c3 {
    height: 85%;
  }

  &.c4 {
    height: 100%;
  }
}

.hero-title {
  font-size: 40px;
  max-width: 289px;
  padding: 28px 0px;
  color: white;
  font-family: 'Apfel Grotezk';
  font-style: normal;
  font-weight: 400;
  line-height: 1;
  letter-spacing: 1.6px;
  margin: 0 auto;
  text-align: center;

  .playfair-text {
    font-family: 'Playfair Display';
    font-size: 40px;
    // NOTE (MAA) : not sure if this is required
    font-style: italic;
    font-weight: 500;
    line-height: 1;
    letter-spacing: 1.6px;
  }

  @media ($sm-min) {
    padding-top: 72px;
    padding-bottom: 48px;
    font-size: 58px;
    max-width: 600px;
    letter-spacing: 3px;
    .playfair-text {
      font-size: 58px;
      letter-spacing: 3px;
    }
  }
  @media ($md-min) {
    padding-top: 72px;
    padding-bottom: 48px;
    font-size: 75px;
    max-width: 600px;
    letter-spacing: 3px;
    .playfair-text {
      font-size: 75px;
      letter-spacing: 3px;
    }
  }

  @media ($lg-min) {
    max-width: unset;
  }
  @media ($xl-min) {
    padding-top: 261px;
  }
}

.hero-sub-title {
  color: var(--color-ultra-light);
  padding: 24px var(--gap) 0px var(--gap);
  margin-left: auto;
  margin-right: auto;
  margin-bottom: var(--gap-xlarge);
  text-align: center;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  white-space: break-spaces;

  & > *:not(:last-child) {
    margin-bottom: var(--hc-vertical-margin-separator);
  }

  @media ($sm-min) {
    font-size: 22px;
  }
  @media ($md-min) {
    padding: 48px var(--gap) 0px var(--gap);
    font-size: 26px;
    max-width: 640px;
  }
  @media ($lg-min) {
    max-width: unset;
  }
}

.hero-call-to-action {
  padding: 0px var(--gap) 0px var(--gap);
  max-width: 400px;
  margin: 0 auto;
  @media ($sm-min) {
    padding: 0;
  }

  .cta-btn {
    border-radius: 50px;

    height: 48px;
    margin-top: 36px;
    margin-bottom: 36px;
    ::v-deep .v-btn__content {
      font-size: 16px;
    }

    @media ($md-min) {
      height: 60px;
      margin-top: 48px;
      margin-bottom: 0px;
      ::v-deep .v-btn__content {
        font-size: 20px;
      }
    }
  }
}

.hellocard-info {
  max-width: var(--page-content-max-width);
  margin-left: auto;
  margin-right: auto;

  padding: 16px 32px;
  margin-bottom: 36px;

  @media ($md-min) {
    padding: 48px;
    margin-bottom: 72px;
  }
  @media ($lg-min) {
    padding: 56px;
    margin-bottom: 112px;
  }
}

.corporate-section {
  padding: var(--gap);

  max-width: min(calc(100% - var(--gap) - var(--gap)), var(--page-content-max-width));

  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr auto;

  margin-left: auto;
  margin-right: auto;

  margin-bottom: 48px;

  border-radius: 20px;
  background-color: white;
  box-shadow: 0px 8px 12px 0px rgba(106, 104, 173, 0.12);

  @media ($sm-min) {
    padding: var(--gap-large);
  }

  @media ($md-min) {
    padding: unset;

    border-radius: unset;
    background-color: unset;
    box-shadow: unset;
    margin-bottom: 72px;
  }

  @media ($lg-min) {
    grid-template-columns: 800px 1fr;
    margin-bottom: 112px;
  }

  .abstract-bezier-curve {
    position: absolute;
    left: -20%;
    top: -40%;
    width: 140%;

    @media ($md-min) {
      left: -10%;
      top: -40%;
      width: 120%;
    }

    @media ($lg-min) {
      left: -10%;
      top: -50%;
      width: 110%;
    }
  }

  .container-bezier-curve {
    position: absolute;
    width: 100%;
    height: 100%;
    overflow: hidden;

    border-radius: 12px;

    @media ($lg-min) {
      border-radius: 20px;
    }
  }

  .corporate-cropped-container {
    grid-column-start: 1;
    grid-column-end: 3;

    grid-row-start: 1;
    grid-row-end: 1;

    position: relative;
    gap: var(--gap-large);

    width: 100%;

    background-origin: border-box;
    background-attachment: scroll;

    background-clip: border-box;

    background: linear-gradient(292deg, var(--Aqua-Gradient, #97e5f5) 3.61%, var(--Pink-Gradient, #ffdfe3) 119.19%);

    border-radius: 12px;

    @media ($md-min) {
      min-height: 417px;
    }

    @media ($lg-min) {
      min-height: 522px;
      border-radius: 20px;
    }
  }

  .corporate-gift-card-table {
    margin-top: auto;
    margin-bottom: auto;
    margin-left: var(--gap-xxlarge);
    text-align: center;

    padding-left: 5%;

    z-index: 2;

    grid-row-start: 1;
    grid-row-end: 1;

    grid-column-start: 1;
    grid-column-end: 3;

    @media ($md-min) {
      height: unset;
      grid-column-start: 1;
      grid-column-end: 1;

      text-align: left;

      padding-bottom: var(--gap-xlarge);

      padding-left: var(--gap);
    }

    @media ($lg-min) {
      padding-left: 150px;
      padding-top: 0;

      text-align: center;

      margin-left: unset;
    }

    .corporate-gift-card-table-img {
      width: unset;

      max-width: 100%;

      @media ($md-min) {
        width: 450px;
        height: unset;
      }

      @media ($lg-min) {
        width: 650px;
        margin-top: -50px;
      }
    }
  }

  .corporate-info {
    grid-column-start: 1;
    grid-column-end: 3;

    @media ($md-min) {
      grid-column-start: 2;
      grid-column-end: 3;

      grid-row-start: 1;
      grid-row-end: 1;

      top: 0;
      z-index: 4;

      padding: var(--gap-xxlarge);
    }

    @media ($lg-min) {
      height: 100%;

      display: flex;
      flex-direction: column;
      justify-content: center;

      margin-left: var(--gap-xlarge);
    }

    .corporate-info-content {
      margin: auto;

      h3 {
        width: 320px;
        text-align: center;
        margin: auto;

        padding-top: 24px;
        padding-bottom: var(--gap-xxlarge);

        line-height: 130%;
        font-weight: 500;

        font-size: 36px;

        @media ($sm-min) {
          font-size: 38px;

          margin: inherit;
          width: unset;
        }

        @media ($md-min) {
          text-align: left;
          padding-bottom: 16px;
        }

        @media ($lg-min) {
          font-size: 48px;
          padding-bottom: 24px;
        }
      }

      p {
        line-height: 130%;
        font-size: 18px;
        text-align: center;
        margin-bottom: 48px;

        @media ($sm-min) {
          font-size: 24px;
        }

        @media ($md-min) {
          margin-bottom: 36px;
          text-align: left;
        }

        @media ($lg-min) {
          margin-bottom: var(--gap-xxlarge);
          font-size: 28px;
        }
      }
    }

    .corporate-cta {
      width: 100%;
      max-width: 450px;
      margin: auto;

      .btn {
        font-size: 18px;
      }

      @media ($sm-min) {
        padding-bottom: var(--gap-large);
      }

      @media ($md-min) {
        padding-top: 0;
        padding-bottom: unset;
        max-width: unset;
        margin: unset;
      }

      @media ($lg-min) {
        max-width: 360px;
      }

      .btn {
        height: 48px;
        border-radius: 50px;

        @media ($sm-min) {
          height: 60px;
        }
      }
    }
  }

  .business-man {
    position: absolute;
    z-index: 2;

    height: 90%;

    left: -5%;
    bottom: 0px;

    @media ($md-min) {
      height: 350px;

      left: -38.5px;
      bottom: 0px;
    }
    @media ($lg-min) {
      height: 471px;

      left: -50px;
      bottom: 0px;
    }
  }
}

.text-section {
  & > *:not(:last-child) {
    margin-bottom: 24px;
    @media ($md-min) {
      margin-bottom: 29.27px;
    }
  }
  margin-bottom: 36px;
  @media ($md-min) {
    margin-bottom: 0px;
  }
  max-width: 1050px;
}

.receive-hello-card-section-2 {
  @media ($md-min) {
    display: flex;
    padding: 0;
    gap: 60px;
    flex-direction: row-reverse;
  }
  @media ($content-min) {
    gap: 146px;
  }

  .image-section-2 {
    display: flex;
    align-self: center;
    background-image: url('~@/assets/home_highlight/hello_card_pay.png');
    background-size: contain;
    background-position: center center;
    text-align: center;
    min-width: 220px;
    min-height: 157px;

    @media ($md-min) {
      height: 259px;
    }
    @media ($content-min) {
      text-align: start;
      height: 291px;
    }
  }
  .text-section {
    max-width: 998px;
  }
}

.receive-hello-card-section {
  margin-bottom: calc(1.5 * var(--hc-vertical-margin-separator));

  padding-bottom: 132px;
  @media ($md-min) {
    margin-bottom: 90px;
    padding-bottom: 60px;
    display: flex;
    padding: 0;
    gap: 84px;
  }
  @media ($lg-min) {
    margin-bottom: 112px;
  }

  @media ($content-min) {
    gap: 200px;
  }

  .image-section {
    --image-section-height: 148px;
    width: 10px;
    left: calc(50% - calc(var(--image-section-height) * 153 / 245 / 2));
    min-height: 100px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      background-image: url('~@/assets/home_highlight/Framenot rotated.png');
      background-color: #6ecafd;
      transform: rotate(10.943deg);
      width: auto;
      border-radius: 6%;
      left: 10%;
      top: 14px;
      aspect-ratio: 153/245;
      background-size: contain;
      box-shadow: 0px 8px 67px 0px #a3baeb;
      height: calc(var(--image-section-height) - 20px);
    }

    @media ($md-min) {
      --image-section-height: 240px;
      left: unset;
      display: flex;
      margin: auto 0;
      margin-right: 10%;
      min-width: 89px;
      height: var(--image-section-height);
    }
    @media ($content-min) {
      --image-section-height: 269px;
      text-align: start;
      min-width: 120px;
      height: var(--image-section-height);
    }
  }
}

.receive-hello-card-section-text {
  color: var(--color-grey-3);

  font-family: 'Barlow';
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%;

  @media ($md-min) {
    font-size: 24px;
  }
  @media ($content-min) {
    font-size: 28px;
  }
}

.receive-hello-card-section-title {
  --hc-card-title: 40px;
  @media ($md-min) {
    --hc-card-title: 50px;
  }
  @media ($content-min) {
    --hc-card-title: 70px;
  }

  font-synthesis: none;
  font-family: 'Apfel Grotezk';
  font-size: var(--hc-card-title);
  font-style: normal;
  font-weight: 400;
  line-height: 110%;
  color: var(--color-secondary);

  .title-accent {
    font-family: 'Playfair Display';
    font-size: var(--hc-card-title);

    font-style: italic;
    font-weight: 600;
    line-height: 110%;
  }
}

.faq {
  --faq-text-font-size: 16px;

  margin: var(--gap);
  display: flex;

  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  border-radius: 20px;
  background-color: white;
  box-shadow: 0px 8px 12px 0px rgba(106, 104, 173, 0.12);
  padding: 12px;
  max-width: min(calc(100% - var(--gap) - var(--gap)), var(--page-content-max-width));

  @media ($sm-min) {
    padding: 36px;
    margin: var(--gap);
    --faq-text-font-size: 18px;
  }

  @media ($md-min) {
    padding: 48px;
    margin: 0 var(--gap);
    --faq-text-font-size: 24px;
  }

  @media ($lg-min) {
    padding: 56px;
    margin: 0 auto;
  }
}

.faq-title {
  font-family: 'Apfel Grotezk';
  font-size: 36px;

  @media ($sm-min) {
    font-size: 38px;
  }

  @media ($md-min) {
    font-size: 48px;
  }
}

.faq-link {
  color: var(--color-grey-3);
  font-size: var(--faq-text-font-size);
  line-height: 36px;
  text-decoration: underline !important;
  font-style: normal;
  font-weight: 600;
  vertical-align: middle;

  &.disabled {
    pointer-events: none;
    cursor: default;
    background-color: transparent !important;
    color: rgba(255, 255, 255, 0.26) !important;
  }
}
.faq-link ::v-deep .v-btn__content {
  line-height: 29px;
}

.faq-footer {
  font-size: var(--faq-text-font-size);
  color: var(--color-grey-3);
}

.expansion-content {
  & > *:not(:last-child) {
    margin-bottom: 16px;
    @media ($sm-min) {
      margin-bottom: 24px;
    }
  }
}

.v-expansion-panel-content {
  font-size: var(--font-size-text-3);

  ::v-deep .v-expansion-panel-content__wrap {
    padding-left: 0;
    padding-top: var(--gap);
    padding-right: var(--gap-xxlarge);

    @media ($md-min) {
      padding-right: var(--gap-xlarge);
    }
  }
}

.v-expansion-panel-header {
  padding: var(--gap) 0;
  min-height: unset;

  @media ($md-min) {
    padding: var(--gap-small) 0;
  }
  .header-container {
    display: flex;
    align-items: flex-start;
  }

  .header-text {
    color: var(--color-grey-3);
    padding-top: calc(16px * 0.5);
    font-size: var(--faq-text-font-size);
    margin-right: 16px;
    font-weight: 500;
    flex: 1 1 auto;

    @media ($md-min) {
      margin-bottom: 0;
      margin-right: 16px;
    }
  }
  .header-icon {
    color: var(--color-grey-3);
    font-size: 31px;
  }
}
</style>
